import { useMemo, useState } from "react";

import { faRefresh } from "@fortawesome/pro-regular-svg-icons";
import { endOfToday, startOfToday, sub } from "date-fns";
import { intersection } from "lodash";
import { twJoin } from "tailwind-merge";

import { FlowVersionStatus } from "src/api/flowTypes";
import { SubHeader, SubHeaderFilter } from "src/flow/SubHeader";
import { useJobs } from "src/jobs/api/queries";
import { tableWidth } from "src/layout/constants";
import {
  PerformanceSource,
  PerformanceSourcePill,
} from "src/performance/ApiJobsPill";
import { DecisionsChart } from "src/performance/charts/DecisionsChart";
import { ErrorsChart } from "src/performance/charts/ErrorsChart";
import { LatencyChart } from "src/performance/charts/LatencyChart";
import { useMetricsRefresh } from "src/performance/MetricsRefresh";
import { OverviewPane } from "src/performance/OverviewPane";
import { useAvailableVersions } from "src/performance/queries";
import { useVersionPicker } from "src/performance/useVersionPicker";
import { useFlowContext } from "src/router/routerContextHooks";
import { formatDate } from "src/utils/datetime";
import { useTimeWindow } from "src/utils/timeWindow";

export const PerformanceContent: React.FC = () => {
  const { flow, workspace } = useFlowContext();
  const { refresh, refreshing, dataUpdatedAt } = useMetricsRefresh();

  // Get IDs of published/archived versions for querying available versions
  const publishedOrArchivedVersionsIds = useMemo(
    () =>
      flow.versions
        .filter(
          (v) =>
            v.status === FlowVersionStatus.PUBLISHED ||
            v.status === FlowVersionStatus.ARCHIVED,
        )
        .map((v) => v.id),
    [flow.versions],
  );

  const { dateRangePickerValue, onDateRangePickerChange, timeWindow } =
    useTimeWindow(
      {
        from: sub(startOfToday(), { weeks: 1 }),
        to: endOfToday(),
      },
      "performance-time-window",
    );

  const [versions, setVersions] = useState<string[]>(
    () => publishedOrArchivedVersionsIds,
  );
  const availableVersions = useAvailableVersions(workspace.base_url, {
    versions: publishedOrArchivedVersionsIds,
    timeWindow,
    flowId: flow.id,
  });

  const filters = useMemo(
    () => ({
      timeWindow,
      flowId: flow.id,
      versions:
        availableVersions.isFetching || !availableVersions.data
          ? // Empty versions list disables getting chart data.
            // This is intendent, because we don't want to fetch data until we know available versions
            []
          : intersection(versions, availableVersions.data),
    }),
    [
      availableVersions.data,
      availableVersions.isFetching,
      timeWindow,
      versions,
      flow.id,
    ],
  );

  const { colors, options } = useVersionPicker({
    flowVersions: flow.versions,
    allAvailableVersions: availableVersions.data ?? [],
  });

  const [source, setSource] = useState<PerformanceSource>(
    PerformanceSource.API,
  );
  const jobs = useJobs(workspace.base_url ?? "", flow.id);
  const hasJobs = jobs.data && jobs.data?.length > 0;

  return (
    <>
      <SubHeader
        title="Performance"
        titleAction={
          hasJobs && (
            <PerformanceSourcePill
              value={source}
              onChange={(source) => {
                setSource(source);
              }}
            />
          )
        }
        paddedParent
      >
        <SubHeader.DatePicker
          rangeLimitInDays={30}
          value={dateRangePickerValue}
          onChange={onDateRangePickerChange}
        />
        {options && (
          <SubHeaderFilter
            disabled={options.length < 1 || source === PerformanceSource.JOBS}
            options={options}
            selected={filters.versions}
            tooltip={(() => {
              if (options.length < 1) {
                return "There is no data to filter for the selected dates";
              }
              return source === PerformanceSource.JOBS
                ? "Version filter is not available for Decisions from Jobs"
                : "";
            })()}
            multiple
            onChange={(value) => {
              setVersions(value ?? []);
            }}
          />
        )}
        <SubHeader.Button
          disabled={refreshing}
          icon={faRefresh}
          spin={refreshing}
          tooltip={
            dataUpdatedAt
              ? `Last updated at ${formatDate(dataUpdatedAt, "h:mm aaa")}`
              : "Refetch"
          }
          onClick={refresh}
        />
      </SubHeader>
      <div
        className={twJoin(
          "mx-auto flex w-full min-w-[1000px] flex-col gap-6",
          tableWidth,
        )}
      >
        <OverviewPane filters={filters} source={source} />

        {source === PerformanceSource.API && (
          <>
            <DecisionsChart colors={colors} filters={filters} />

            <ErrorsChart filters={filters} />

            <LatencyChart colors={colors} filters={filters} />
          </>
        )}
      </div>
    </>
  );
};
